/**
 * A meeting's session, where a meeting runs: start, notes, items, end, summary. One component, two
 * data sources (TMMAPP-0159 §D 22): a virtual account's meeting (`/v/{va}/meetings/{mid}/{occ}`,
 * its money first) or a standalone one (`/me/meetings/{mid}/{occ}`, `home` null), which is talk only:
 * its workspace serves no money section, so none renders, and the agenda leads instead. Its
 * subtitle reads "With {names}" there; a 404 means access ended (the not-found state).
 */
import { MoneyText } from '@/components/MoneyText';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shell/NotifyProvider';
import { virtualAccountsApi, type VirtualAccountDetail } from '@/api/virtualAccounts';
import { isApiError, isNotFound } from '@/api/errors';
import {
  meetingsApi,
  type Meeting,
  type MeetingHome,
  type MeetingMoney,
  type MeetingOccurrenceDetail,
  type MeetingRecording,
  type MeetingSummary,
} from '@/api/meetings';
import { cardGridSx } from '@/components/cardGrid';
import { ErrorState } from '@/components/ErrorState';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HeadedCard } from '@/components/HeadedCard';
import { useConfirm } from '@/components/useConfirm';
import { StatusChip } from '@/components/StatusChip';
import { PlanRegister } from '@/features/cashflow/PlanRegister';
import { ProjectionChart } from '@/features/cashflow/ProjectionChart';
import { viewFromPlan } from '@/features/cashflow/types';
import { EnvelopeCard } from '@/features/virtualAccount/EnvelopeCard';
import { parseRouteId } from '@/features/virtualAccount/routeId';
import { formatCivilDate, formatInstant } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { NotFoundBody, RecordNotFound } from '@/routes/NotFound';
import { personName } from '@/i18n/personName';
import { useMe } from '@/session/meHooks';
import { OverflowMenu } from '@/shell/OverflowMenu';
import { PageHeader } from '@/shell/PageHeader';
import { usePageTitle } from '@/shell/usePageTitle';
import { enumParam, useUrlState } from '@/shared/useUrlState';
import { canWriteNotes } from './canWriteNotes';
import { SessionAgenda } from './SessionAgenda';
import { canWriteStandalone, meetingWithLabel } from './standalone';
import { SessionAllocateDialog } from './SessionAllocateDialog';
import { noteMeetingWrite } from './meetingWrites';
import { SessionContext } from './sessionContext';
import { SessionItems } from './SessionItems';
import { SessionNotes } from './SessionNotes';
import { SessionRecordings } from './SessionRecordings';
import { SessionSummaries } from './SessionSummaries';

const SECTIONS = ['money', 'notes', 'items', 'summaries'] as const;
type SessionTab = (typeof SECTIONS)[number];
const SECTION_LABEL = {
  money: 'meetings.tabMoney',
  notes: 'meetings.tabNotes',
  items: 'meetings.tabItems',
  summaries: 'meetings.tabSummaries',
} as const satisfies Record<SessionTab, string>;

const URL_SCHEMA = {
  tab: enumParam<SessionTab>(SECTIONS, 'money'),
};

/** `standalone`: the `/me/meetings/:meetingId/:occurrenceKey` route (no virtual account). */
export function SessionPage({ standalone = false }: { standalone?: boolean }) {
  const params = useParams();
  const virtualAccountId = standalone ? null : parseRouteId(params.virtualAccountId);
  const meetingId = parseRouteId(params.meetingId);
  const rawKey = params.occurrenceKey;
  if ((!standalone && virtualAccountId === null) || meetingId === null || rawKey === undefined)
    return <RecordNotFound />;
  // Keyed per occurrence, so a hop to another one starts from a fresh load.
  return (
    <SessionView
      key={`${virtualAccountId ?? 'me'}:${meetingId}:${rawKey}`}
      home={virtualAccountId}
      meetingId={meetingId}
      occurrenceKey={rawKey}
    />
  );
}

function SessionView({
  home,
  meetingId,
  occurrenceKey,
}: {
  home: MeetingHome;
  meetingId: number;
  occurrenceKey: string;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const me = useMe();
  const theme = useTheme();
  const lg = useMediaQuery(theme.breakpoints.up('lg'));
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const [detail, setDetail] = useState<MeetingOccurrenceDetail | null>(null);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [virtualAccount, setVirtualAccount] = useState<VirtualAccountDetail | null>(null);
  const [summaries, setSummaries] = useState<MeetingSummary[]>([]);
  const [recordings, setRecordings] = useState<MeetingRecording[]>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'notFound' | 'error'>('loading');
  const [loadError, setLoadError] = useState<unknown>(undefined);
  const [{ tab }, setUrl] = useUrlState(URL_SCHEMA);
  const [allocate, setAllocate] = useState(false);
  const notify = useNotify();
  // Ending a meeting can't be undone (the occurrence becomes held): it confirms first.
  const ending = useConfirm<true>();

  const load = useCallback(async () => {
    try {
      const [occ, meet, virtualAccountDetail] = await Promise.all([
        meetingsApi.getOccurrence(home, meetingId, occurrenceKey),
        meetingsApi.get(home, meetingId),
        // A standalone meeting has no virtual account to read.
        home === null ? Promise.resolve(null) : virtualAccountsApi.get(home),
      ]);
      setDetail(occ);
      setMeeting(meet);
      setVirtualAccount(virtualAccountDetail);
      const [sums, recs] = await Promise.all([
        meetingsApi.listSummaries(home, meetingId, occurrenceKey).catch(() => []),
        meetingsApi.listRecordings(home, meetingId, occurrenceKey).catch(() => []),
      ]);
      setSummaries(sums);
      setRecordings(recs);
      setStatus('ready');
    } catch (error) {
      setLoadError(error);
      // Access is live: a 404 on a standalone meeting means it ended (TMMAPP-0159 §D 23).
      setStatus(isApiError(error) && isNotFound(error) ? 'notFound' : 'error');
    }
  }, [home, meetingId, occurrenceKey]);

  const fetchKey = `${home ?? 'me'}:${meetingId}:${occurrenceKey}`;
  const lastFetch = useRef<string | null>(null);
  useEffect(() => {
    if (lastFetch.current === fetchKey) return;
    lastFetch.current = fetchKey;
    void load();
  }, [fetchKey, load]);

  const dateLabel =
    detail?.occurrence.startsAt != null
      ? formatInstant(
          detail.occurrence.startsAt,
          locale,
          meeting?.seriesTimezone ?? me.user.timezone ?? 'UTC',
          {
            dateStyle: 'medium',
          },
        )
      : '';
  const title = usePageTitle({
    title: meeting
      ? [meeting.title, dateLabel].filter((bit) => bit.length > 0).join(' · ')
      : status === 'notFound' && home !== null
        ? t('notFound.title')
        : status === 'error' || status === 'notFound'
          ? t('pageHeader.fallback.meeting')
          : null,
    fallback: t('pageHeader.fallback.meeting'),
  });
  // A standalone meeting's subtitle is "With {names}" (§D 22): its people, never a virtual account.
  const withLabel =
    home === null && meeting !== null ? meetingWithLabel(meeting, me.user.id, t, locale) : null;
  const virtualAccountLabel =
    virtualAccount?.name ??
    me.virtualAccounts.find((row) => row.id === home)?.name ??
    t('pageHeader.fallback.virtualAccount');
  // "← {virtual account}", to its meetings (TMM-B7 1.4); a standalone meeting's is My Money Meetings.
  const back =
    home === null
      ? { label: t('nav.meetings'), to: '/me/meetings' }
      : { label: virtualAccountLabel, to: `/v/${home}?tab=meetings` };

  // Every state renders the same `PageHeader` first, so the `h1` (and route focus on it) survives
  // loading → ready / error / not found.
  if (status === 'notFound') {
    // A standalone meeting you've lost access to says so, with the way back (TMMAPP-0160 item 11).
    if (home === null) {
      return (
        <>
          <PageHeader title={title} back={back} />
          <Typography variant="body1">{t('meetings.noLongerAvailable')}</Typography>
        </>
      );
    }
    return (
      <>
        <PageHeader title={title} />
        <NotFoundBody />
      </>
    );
  }
  if (status === 'error') {
    return (
      <>
        <PageHeader title={title} back={back} />
        <ErrorState
          error={loadError}
          what={t('errors.what.meetingSession')}
          onRetry={() => void load()}
        />
      </>
    );
  }
  if (
    status === 'loading' ||
    detail === null ||
    meeting === null ||
    (home !== null && virtualAccount === null)
  ) {
    return (
      <>
        <PageHeader title={title} back={back} />
        <Typography>{t('common.loading')}</Typography>
      </>
    );
  }

  const occ = detail.occurrence;
  const live = occ.heldStartedAt !== null && occ.heldEndedAt === null;
  const organizesStandalone = me.user.id !== null && meeting.organizer.userId === me.user.id;
  // AI is the owner's on a virtual account; on a standalone meeting, its organizer's (who pays).
  const owner = virtualAccount !== null ? virtualAccount.isOwner : organizesStandalone;
  // Meeting writes are the owner's and every Manager's, guest seats included (MM-D-115 supersedes
  // MM-D-32); on a standalone meeting the organizer's, a subscriber's and a guest Manager's (§D 23).
  const writers =
    virtualAccount !== null
      ? canWriteNotes(virtualAccount)
      : canWriteStandalone(me, organizesStandalone);
  const currency = virtualAccount?.currency ?? null;
  const people =
    virtualAccount !== null
      ? [
          { userId: meeting.organizer.userId, displayName: meeting.organizer.displayName },
          ...virtualAccount.shares
            .filter((s) => s.status === 'ACTIVE' && s.userId !== null)
            .map((s) => ({
              userId: s.userId,
              displayName: personName(s.displayName, t),
            })),
        ]
      : [
          meeting.organizer,
          ...meeting.invitees.filter((invite) => invite.status !== 'CANCELLED'),
        ].map((person) => ({
          userId: person.userId,
          displayName: personName(person.displayName, t),
        }));
  // Only people with an id can own an item (someone outside the viewer's pool has none).
  const assignable = people.flatMap((person) =>
    person.userId === null ? [] : [{ userId: person.userId, displayName: person.displayName }],
  );
  const address = { virtualAccountId: home, meetingId, occurrenceKey };
  // Talk only: a standalone meeting's workspace has no money section, so nothing renders for it.
  const moneyWorkspace = detail.workspace.money;

  const sessionActions = writers;

  async function runStart() {
    try {
      await meetingsApi.start(home, meetingId, occurrenceKey);
      noteMeetingWrite();
      notify(t('meetings.started'));
      await load();
    } catch {
      notify(t('meetings.errorGeneric'), { severity: 'error' });
    }
  }

  const money =
    moneyWorkspace === null || virtualAccount === null ? null : (
      <SessionMoney
        workspace={moneyWorkspace}
        virtualAccountId={virtualAccount.id}
        currency={virtualAccount.currency}
        md={md}
      />
    );
  const agenda =
    home === null ? (
      <SessionAgenda
        meetingId={meetingId}
        agenda={detail.workspace.agenda}
        canWrite={writers}
        onChanged={() => void load()}
      />
    ) : null;

  const notes = (
    <SessionNotes
      virtualAccountId={home}
      meetingId={meetingId}
      occurrenceKey={occurrenceKey}
      notes={detail.workspace.notes}
      canWrite={writers}
      onChanged={() => void load()}
    />
  );
  const items = (
    <SessionItems
      items={detail.workspace.items}
      address={address}
      canWrite={writers}
      people={assignable}
      envelopes={moneyWorkspace?.envelopes ?? []}
      currency={currency}
      onChanged={() => void load()}
    />
  );
  const sums = (
    <Stack spacing={3}>
      <SessionSummaries
        virtualAccountId={home}
        meetingId={meetingId}
        occurrenceKey={occurrenceKey}
        summaries={summaries}
        canWrite={writers}
        organizer={owner}
        address={address}
        people={assignable}
        onChanged={() => void load()}
      />
      <SessionRecordings
        virtualAccountId={home}
        meetingId={meetingId}
        occurrenceKey={occurrenceKey}
        recordings={recordings}
        canWrite={writers}
        onChanged={() => void load()}
      />
    </Stack>
  );
  // A standalone session has no Money section: its first section is Notes (the agenda above them).
  const sections = money === null ? SECTIONS.filter((key) => key !== 'money') : SECTIONS;
  const shownTab: SessionTab = money === null && tab === 'money' ? 'notes' : tab;
  const talk = (
    <>
      {agenda}
      {notes}
    </>
  );

  return (
    <>
      <PageHeader
        title={title}
        back={back}
        subtitle={
          live
            ? t('meetings.liveSince', {
                time: formatInstant(occ.heldStartedAt!, locale, meeting.seriesTimezone),
              })
            : (virtualAccount?.name ?? withLabel ?? undefined)
        }
        // The status only: no privacy pill under the title (TMMAPP-0161 item 3).
        status={<StatusChip domain="meeting" status={live ? 'IN_PROGRESS' : occ.status} />}
        menu={
          sessionActions ? (
            <OverflowMenu
              variant="page"
              title={meeting.title}
              label={t('meetings.session.actions')}
            >
              {(close) => [
                writers && occ.status === 'SCHEDULED' && !live ? (
                  <MenuItem
                    key="start"
                    onClick={() => {
                      close();
                      void runStart();
                    }}
                  >
                    {t('meetings.start')}
                  </MenuItem>
                ) : null,
                writers && live ? (
                  <MenuItem
                    key="end"
                    onClick={() => {
                      close();
                      ending.ask(true);
                    }}
                  >
                    {t('meetings.end')}
                  </MenuItem>
                ) : null,
                // Allocating is money: a standalone meeting has none.
                writers && money !== null ? (
                  <MenuItem
                    key="allocate"
                    onClick={() => {
                      close();
                      setAllocate(true);
                    }}
                  >
                    {t('meetings.allocateInSession')}
                  </MenuItem>
                ) : null,
              ]}
            </OverflowMenu>
          ) : undefined
        }
      />
      {ending.target !== null && (
        <ConfirmDialog
          title={t('meetings.endTitle', { title: meeting.title })}
          consequence={t('meetings.endConsequence')}
          confirmLabel={t('meetings.end')}
          cancelLabel={t('meetings.keepOpen')}
          destructive
          busy={ending.busy}
          error={ending.error}
          onConfirm={() =>
            void ending.run(
              async () => {
                await meetingsApi.end(home, meetingId, occurrenceKey);
                noteMeetingWrite();
                notify(t('meetings.ended'));
                await load();
              },
              () => t('meetings.errorGeneric'),
            )
          }
          onCancel={ending.cancel}
        />
      )}
      <SessionContext.Provider value={{ meetingOccurrenceId: occ.occurrenceId }}>
        <Box>
          {lg ? (
            <Grid container spacing={3}>
              <Grid size={7}>{money ?? <Stack spacing={4}>{talk}</Stack>}</Grid>
              <Grid size={5}>
                <Stack spacing={4}>
                  {money !== null && notes}
                  {items}
                  {sums}
                </Stack>
              </Grid>
            </Grid>
          ) : md ? (
            <Stack spacing={4}>
              <Stack
                direction="row"
                sx={{ position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1, gap: 1 }}
              >
                {sections.map((key) => (
                  <Button
                    key={key}
                    size="small"
                    onClick={() => document.getElementById(`session-${key}`)?.scrollIntoView()}
                  >
                    {t(SECTION_LABEL[key])}
                  </Button>
                ))}
              </Stack>
              {money}
              {talk}
              {items}
              {sums}
            </Stack>
          ) : (
            <>
              <Tabs
                value={shownTab}
                onChange={(_event, next: SessionTab) => setUrl({ tab: next }, { push: true })}
                variant="scrollable"
                scrollButtons="auto"
                sx={{ mb: 2 }}
              >
                {sections.map((key) => (
                  <Tab key={key} value={key} label={t(SECTION_LABEL[key])} />
                ))}
              </Tabs>
              {shownTab === 'money' && money}
              {shownTab === 'notes' && talk}
              {shownTab === 'items' && items}
              {shownTab === 'summaries' && sums}
            </>
          )}
          {allocate && (
            <SessionAllocateDialog onClose={() => setAllocate(false)} onNotify={notify} />
          )}
        </Box>
      </SessionContext.Provider>
    </>
  );
}

/** A virtual account meeting's money: Unassigned, the envelopes, since last time, looking ahead. */
function SessionMoney({
  workspace,
  virtualAccountId,
  currency,
  md,
}: {
  workspace: MeetingMoney;
  virtualAccountId: number;
  currency: string;
  md: boolean;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const looking = workspace.lookingAhead ? viewFromPlan(workspace.lookingAhead) : null;
  const unassigned = workspace.unassignedMinor;
  return (
    <HeadedCard
      title={t('meetings.session.money')}
      subtitle={t('meetings.session.moneySub')}
      sx={{ scrollMarginTop: 16 }}
      id="session-money"
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant="subtitle2">{t('virtualAccount.unassigned')}</Typography>
          <MoneyText minor={unassigned} currency={currency} variant="figureSm" tone="signed" />
        </Box>
        <Box sx={cardGridSx()}>
          {workspace.envelopes.map((envelope) => (
            <Box key={envelope.id} sx={{ minWidth: 0 }}>
              <EnvelopeCard
                envelope={envelope}
                virtualAccountId={virtualAccountId}
                currency={currency}
                compact
              />
            </Box>
          ))}
        </Box>
        <Box>
          <Typography variant="h3" component="h3" sx={{ mb: 1 }}>
            {t('workspace.sinceLast')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {workspace.sinceLastMeeting.since
              ? t('meetings.sinceLabel', {
                  date: formatCivilDate(workspace.sinceLastMeeting.since, locale),
                })
              : t('meetings.sinceNever')}
          </Typography>
          <Table size="small" aria-label={t('workspace.sinceLast')}>
            <TableHead>
              <TableRow>
                <TableCell>{t('cashflow.colDate')}</TableCell>
                <TableCell>{t('cashflow.colPayee')}</TableCell>
                {!md ? null : <TableCell>{t('cashflow.colEnvelope')}</TableCell>}
                <TableCell align="right">{t('cashflow.colAmount')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {workspace.sinceLastMeeting.register.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatCivilDate(row.postedOn, locale)}</TableCell>
                  <TableCell>{row.payee}</TableCell>
                  {md && (
                    <TableCell>
                      {row.allocations
                        .map((a) => a.envelopeName)
                        .filter(Boolean)
                        .join(', ')}
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <MoneyText minor={row.publishedMinor} currency={currency} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
        {looking && (
          <Box>
            <Typography variant="h3" component="h3" sx={{ mb: 1 }}>
              {t('workspace.lookingAhead')}
            </Typography>
            <ProjectionChart
              from={looking.from}
              to={looking.to}
              series={looking.chartSeries}
              events={looking.events}
              register={looking.register}
              currency={currency}
            />
            <PlanRegister rows={looking.register} currency={currency} />
          </Box>
        )}
        {workspace.summary?.body ? (
          <Box>
            <Typography variant="h3" component="h3" sx={{ mb: 1 }}>
              {t('meetings.summaryTitle')}
            </Typography>
            <Typography sx={{ whiteSpace: 'pre-wrap' }}>{workspace.summary.body}</Typography>
          </Box>
        ) : null}
      </Stack>
    </HeadedCard>
  );
}
